/**
 * Small transient status toast, mounted on document.body so it survives
 * app re-renders (same reasoning as the Turnstile overlay host). The DOM
 * surface is injectable for the node-environment test suite.
 */

export type ToastKind = 'success' | 'error';

type ToastElement = Pick<HTMLElement, 'className' | 'textContent' | 'setAttribute' | 'remove'>;

export interface ToastEnv {
  createElement(tag: string): ToastElement;
  append(el: ToastElement): void;
  setTimeout(fn: () => void, ms: number): void;
}

function browserToastEnv(): ToastEnv {
  return {
    createElement: (tag) => document.createElement(tag),
    append: (el) => document.body.appendChild(el as HTMLElement),
    setTimeout: (fn, ms) => void window.setTimeout(fn, ms),
  };
}

export const TOAST_DURATION_MS = 2_000;

export function showToast(message: string, kind: ToastKind = 'success', env: ToastEnv = browserToastEnv()): void {
  const toast = env.createElement('div');
  toast.className = `toast toast--${kind}`;
  toast.setAttribute('role', 'status');
  toast.textContent = message;
  env.append(toast);
  env.setTimeout(() => toast.remove(), TOAST_DURATION_MS);
}

export const CONTRACT_COPIED_MESSAGE = 'contract address copied';
export const CONTRACT_COPY_FAILED_MESSAGE = 'could not copy contract address';

/**
 * Copy the public $OBOL contract (mint) address and confirm with a toast.
 * Fails soft: a missing or rejecting clipboard shows the failure toast and
 * never throws into the click handler.
 */
export async function copyContractAddress(
  value: string,
  writeText: ((text: string) => Promise<void>) | undefined,
  toast: (message: string, kind: ToastKind) => void = showToast,
): Promise<void> {
  if (!writeText) {
    toast(CONTRACT_COPY_FAILED_MESSAGE, 'error');
    return;
  }
  try {
    await writeText(value);
    toast(CONTRACT_COPIED_MESSAGE, 'success');
  } catch {
    toast(CONTRACT_COPY_FAILED_MESSAGE, 'error');
  }
}
